const RIFT_REVIEW_DRAG_THRESHOLD = 0.18;
const RIFT_REVIEW_VELOCITY_THRESHOLD = 0.35;
const RIFT_REVIEW_AXIS_THRESHOLD = 6;
const RIFT_REVIEW_DESKTOP_QUERY = '(min-width: 750px)';

class RiftJewelryReviews extends HTMLElement {
  #abortController;
  #animationFrame;
  #autoplayTimer;
  #connectionFrame;
  #connectionObserver;
  #currentIndex = 0;
  #dragController;
  #dragState;
  #hasFocus = false;
  #infinite = true;
  #isHovered = false;
  #isMeasuring = false;
  #measureFrame;
  #motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  #mutationObserver;
  #originalSlides = [];
  #physicalSlides = [];
  #position = 0;
  #resizeObserver;
  #section;
  #slideWidth = 1;
  #step = 1;
  #suppressClick = false;
  #track;
  #transitionDuration = 450;
  #viewport;
  #viewportWidth = 0;
  #visibleCards = 3;

  connectedCallback() {
    this.#connectionObserver?.disconnect();
    this.#connectionObserver = new MutationObserver(() => this.#initializeWhenReady());
    this.#connectionObserver.observe(this, { childList: true, subtree: true });

    if (this.#connectionFrame) cancelAnimationFrame(this.#connectionFrame);
    this.#connectionFrame = requestAnimationFrame(() => {
      this.#connectionFrame = undefined;
      this.#initializeWhenReady();
    });
  }

  disconnectedCallback() {
    if (this.#connectionFrame) cancelAnimationFrame(this.#connectionFrame);
    if (this.#measureFrame) cancelAnimationFrame(this.#measureFrame);
    this.#connectionFrame = undefined;
    this.#measureFrame = undefined;
    this.#connectionObserver?.disconnect();
    this.#connectionObserver = undefined;
    this.#abortController?.abort();
    this.#dragController?.abort();
    this.#resizeObserver?.disconnect();
    this.#mutationObserver?.disconnect();
    this.#cancelAnimation();
    this.#clearAutoplay();
  }

  #initializeWhenReady() {
    if (!this.isConnected || this.hasAttribute('data-rift-ready')) return;

    const track = this.querySelector('[data-rift-track]');
    const viewport = this.querySelector('[data-rift-viewport]');
    const firstSlide = track?.querySelector('[data-rift-review-slide]');
    if (!track || !viewport || !firstSlide) return;

    this.#connectionObserver?.disconnect();
    this.#connectionObserver = undefined;
    this.#initialize();
  }

  #initialize() {
    try {
      this.#abortController?.abort();
      this.#abortController = new AbortController();
      this.#section = this.querySelector('.rift-jewelry-reviews');
      this.#track = this.querySelector('[data-rift-track]');
      this.#viewport = this.querySelector('[data-rift-viewport]');

      if (!this.#section || !this.#track || !this.#viewport) return;

      this.#infinite = this.dataset.riftInfinite !== 'false';
      this.#transitionDuration = Number.parseInt(this.dataset.riftTransitionDuration || '450', 10) || 450;

      this.#rebuildSlides();
      this.#bindEvents();
      this.#observeLayout();
      this.#scheduleAutoplay();
    } catch (error) {
      this.dataset.riftInitializationError = error instanceof Error ? error.message : 'Unknown error';
      console.error('RIFT testimonial carousel failed to initialize.', error);
    }
  }

  #bindEvents() {
    const { signal } = this.#abortController;

    this.#viewport.addEventListener('pointerdown', this.#onPointerDown, { signal });
    this.#viewport.addEventListener('keydown', this.#onKeyDown, { signal });
    this.#track.addEventListener('click', this.#onSlideClick, { signal });
    this.querySelector('[data-rift-previous]')?.addEventListener('click', () => this.#goBy(-1), { signal });
    this.querySelector('[data-rift-next]')?.addEventListener('click', () => this.#goBy(1), { signal });

    for (const dot of this.querySelectorAll('[data-rift-dot]')) {
      dot.addEventListener('click', () => this.#goTo(Number.parseInt(dot.dataset.riftDot || '0', 10)), { signal });
    }

    this.addEventListener('mouseenter', () => {
      this.#isHovered = true;
      this.#clearAutoplay();
    }, { signal });
    this.addEventListener('mouseleave', () => {
      this.#isHovered = false;
      this.#scheduleAutoplay();
    }, { signal });
    this.addEventListener('focusin', () => {
      this.#hasFocus = true;
      this.#clearAutoplay();
    }, { signal });
    this.addEventListener('focusout', (event) => {
      if (event.relatedTarget && this.contains(event.relatedTarget)) return;
      this.#hasFocus = false;
      this.#scheduleAutoplay();
    }, { signal });
    document.addEventListener('visibilitychange', this.#onVisibilityChange, { signal });
    document.addEventListener('shopify:block:select', this.#onBlockSelect, { signal });
    this.#motionQuery.addEventListener('change', this.#onMotionPreferenceChange, { signal });
  }

  #observeLayout() {
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = new ResizeObserver(() => this.#scheduleMeasure());
    this.#resizeObserver.observe(this.#viewport);
    for (const slide of this.#originalSlides) {
      const card = slide.querySelector('.rift-review-card');
      if (card) this.#resizeObserver.observe(card);
    }

    this.#mutationObserver = new MutationObserver((mutations) => {
      const hasOriginalChange = mutations.some((mutation) =>
        [...mutation.addedNodes, ...mutation.removedNodes].some(
          (node) => node.nodeType === 1 && !node.hasAttribute('data-rift-clone')
        )
      );

      if (hasOriginalChange) this.#rebuildSlides();
    });
    this.#mutationObserver.observe(this.#track, { childList: true });
  }

  #rebuildSlides() {
    const currentBlockId = this.#originalSlides[this.#currentIndex]?.dataset.riftBlockId;

    this.#mutationObserver?.disconnect();
    this.#resizeObserver?.disconnect();
    this.#track.querySelectorAll('[data-rift-clone]').forEach((slide) => slide.remove());
    this.#originalSlides = [
      ...this.#track.querySelectorAll('[data-rift-review-slide]:not([data-rift-clone])'),
    ];

    if (!this.#originalSlides.length) {
      this.removeAttribute('data-rift-ready');
      return;
    }

    const contentDirection = getComputedStyle(this).direction;
    this.#originalSlides.forEach((slide, index) => {
      slide.style.direction = contentDirection;
      slide.dataset.riftVirtualIndex = String(index);
    });

    if (currentBlockId) {
      const preservedIndex = this.#originalSlides.findIndex((slide) => slide.dataset.riftBlockId === currentBlockId);
      this.#currentIndex = preservedIndex >= 0
        ? preservedIndex
        : Math.min(this.#currentIndex, this.#originalSlides.length - 1);
    } else {
      this.#currentIndex = Math.min(this.#currentIndex, this.#originalSlides.length - 1);
    }

    if (this.#infinite && this.#originalSlides.length === 2) {
      const [first, second] = this.#originalSlides;
      this.#track.prepend(
        this.#createClone(first, 0, -2),
        this.#createClone(second, 1, -1)
      );
      this.#track.append(
        this.#createClone(first, 0, 2),
        this.#createClone(second, 1, 3)
      );
    }

    this.#physicalSlides = [...this.#track.querySelectorAll('[data-rift-review-slide]')];
    this.#physicalSlides.forEach((slide, index) => {
      slide.dataset.riftPhysicalIndex = String(index);
    });
    this.#position = this.#currentIndex;
    this.setAttribute('data-rift-ready', '');
    this.toggleAttribute('data-rift-single', this.#originalSlides.length === 1);

    this.#scheduleMeasure();
    this.#updateSettledState();

    if (this.#resizeObserver) {
      this.#resizeObserver.observe(this.#viewport);
      for (const slide of this.#originalSlides) {
        const card = slide.querySelector('.rift-review-card');
        if (card) this.#resizeObserver.observe(card);
      }
    }
    this.#mutationObserver?.observe(this.#track, { childList: true });
  }

  #createClone(slide, originalIndex, virtualIndex) {
    const clone = slide.cloneNode(true);
    clone.setAttribute('data-rift-clone', '');
    clone.dataset.riftOriginalIndex = String(originalIndex);
    clone.dataset.riftVirtualIndex = String(virtualIndex);
    clone.setAttribute('aria-hidden', 'true');
    clone.setAttribute('inert', '');
    clone.removeAttribute('id');
    clone.removeAttribute('data-rift-block-id');

    for (const element of [clone, ...clone.querySelectorAll('*')]) {
      element.removeAttribute('id');
      for (const attribute of [...element.attributes]) {
        if (attribute.name.includes('shopify')) element.removeAttribute(attribute.name);
      }
    }

    return clone;
  }

  #scheduleMeasure() {
    if (this.#measureFrame || !this.isConnected) return;
    this.#measureFrame = requestAnimationFrame(() => {
      this.#measureFrame = undefined;
      this.#measure();
    });
  }

  #measure() {
    if (this.#isMeasuring || !this.#originalSlides.length) return;
    this.#isMeasuring = true;

    const referenceSlide = this.#originalSlides[0];
    const styles = getComputedStyle(this.#section);
    this.#slideWidth = referenceSlide.offsetWidth || 1;
    this.#viewportWidth = this.#viewport.clientWidth;

    const isDesktop = window.matchMedia(RIFT_REVIEW_DESKTOP_QUERY).matches;
    const visibleSetting = isDesktop
      ? this.#section.dataset.riftReviewVisibleDesktop
      : this.#section.dataset.riftReviewVisibleMobile;
    this.#visibleCards = Number.parseInt(visibleSetting || '3', 10) || 3;

    const sideScale = Number.parseFloat(styles.getPropertyValue('--rift-review-side-scale')) || 0.92;
    const sideInsetProperty = isDesktop ? '--rift-review-card-overlap' : '--rift-review-card-inset-mobile';
    const sideInset = Number.parseFloat(styles.getPropertyValue(sideInsetProperty)) || 0;

    if (this.#visibleCards === 3) {
      const centeredInset = Math.max((this.#viewportWidth - this.#slideWidth) / 2, 0);
      const scaledInset = this.#slideWidth * (1 - sideScale) / 2;
      this.#step = Math.max(centeredInset + scaledInset - sideInset, 12);
    } else {
      this.#step = Math.max(this.#slideWidth + 24, this.#viewportWidth * 0.72);
    }

    this.#section.style.setProperty('--rift-review-stage-height', 'auto');
    const maximumHeight = Math.max(
      ...this.#originalSlides.map((slide) => {
        const card = slide.querySelector('.rift-review-card');
        return Math.max(card?.scrollHeight || 0, card?.offsetHeight || 0, slide.scrollHeight || 0);
      }),
      1
    );
    this.#section.style.setProperty('--rift-review-stage-height', `${Math.ceil(maximumHeight)}px`);
    this.#render(this.#position);

    this.#isMeasuring = false;
  }

  #render(position) {
    this.#position = position;
    const count = this.#originalSlides.length;
    if (!count) return;

    const styles = getComputedStyle(this.#section);
    const sideScale = Number.parseFloat(styles.getPropertyValue('--rift-review-side-scale')) || 0.92;
    const sideOpacity = Number.parseFloat(styles.getPropertyValue('--rift-review-side-opacity')) || 0.55;
    const effectStrength = this.#section.dataset.riftReviewCardEffect === 'none'
      ? 0
      : Number.parseFloat(styles.getPropertyValue('--rift-review-card-effect-strength')) || 0;
    const transitionStyle = this.#section.dataset.riftReviewTransition || 'scale';
    const normalizedPosition = this.#mod(position, count);

    this.#physicalSlides.forEach((slide) => {
      const originalIndex = Number.parseInt(
        slide.dataset.riftOriginalIndex ?? slide.dataset.riftReviewIndex ?? '0',
        10
      );
      let delta;

      if (this.#infinite && count === 2) {
        delta = Number.parseFloat(slide.dataset.riftVirtualIndex || '0') - position;
      } else if (this.#infinite && count > 2) {
        delta = originalIndex - normalizedPosition;
        while (delta > count / 2) delta -= count;
        while (delta < count / -2) delta += count;
      } else {
        delta = originalIndex - position;
      }

      const distance = Math.abs(delta);
      const focus = Math.max(0, 1 - Math.min(distance, 1));
      const visibilityRange = this.#visibleCards === 3 ? 2 : 1;
      const edgeVisibility = Math.max(0, Math.min(1, visibilityRange - distance));
      const scale = transitionStyle === 'scale' ? sideScale + (1 - sideScale) * focus : 1;
      const opacity = (sideOpacity + (1 - sideOpacity) * focus) * edgeVisibility;
      const translateY = transitionStyle === 'slide' ? (1 - focus) * 12 : 0;
      const shadowOpacity = effectStrength * (0.14 + 0.86 * focus) * edgeVisibility;

      slide.style.setProperty('--rift-slide-scale', scale.toFixed(4));
      slide.style.setProperty('--rift-slide-opacity', opacity.toFixed(4));
      slide.style.setProperty('--rift-slide-focus', focus.toFixed(4));
      slide.style.setProperty('--rift-slide-translate-x', `${(delta * this.#step).toFixed(2)}px`);
      slide.style.setProperty('--rift-slide-translate-y', `${translateY.toFixed(2)}px`);
      slide.style.setProperty('--rift-slide-shadow-opacity', shadowOpacity.toFixed(4));
      slide.dataset.riftRingDelta = delta.toFixed(4);
      slide.style.visibility = edgeVisibility > 0.001 ? 'visible' : 'hidden';
      slide.style.zIndex = edgeVisibility > 0.001
        ? String(Math.max(1, 3 - Math.min(2, Math.round(distance))))
        : '0';
      slide.style.pointerEvents = edgeVisibility > 0.001 && distance <= 1.15 && !slide.hasAttribute('data-rift-clone')
        ? 'auto'
        : 'none';
    });
  }

  #onPointerDown = (event) => {
    if (this.#originalSlides.length <= 1 || event.button !== 0 || !event.isPrimary) return;

    this.#cancelAnimation();
    this.#clearAutoplay();
    this.#dragController?.abort();
    this.#dragController = new AbortController();
    const { signal } = this.#dragController;

    this.#dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      lastX: event.clientX,
      lastTime: performance.now(),
      startPosition: this.#position,
      velocity: 0,
      active: false,
      cancelled: false,
    };

    document.addEventListener('pointermove', this.#onPointerMove, { signal, passive: false });
    document.addEventListener('pointerup', this.#onPointerEnd, { signal });
    document.addEventListener('pointercancel', this.#onPointerEnd, { signal });
  };

  #onPointerMove = (event) => {
    const drag = this.#dragState;
    if (!drag || drag.cancelled || event.pointerId !== drag.pointerId) return;

    const deltaX = event.clientX - drag.startX;
    const deltaY = event.clientY - drag.startY;

    if (!drag.active) {
      if (Math.abs(deltaY) > RIFT_REVIEW_AXIS_THRESHOLD && Math.abs(deltaY) >= Math.abs(deltaX)) {
        drag.cancelled = true;
        return;
      }
      if (Math.abs(deltaX) < RIFT_REVIEW_AXIS_THRESHOLD || Math.abs(deltaX) <= Math.abs(deltaY)) return;

      drag.active = true;
      this.setAttribute('data-rift-dragging', '');
      this.#viewport.setPointerCapture?.(event.pointerId);
    }

    event.preventDefault();
    const now = performance.now();
    const elapsed = Math.max(now - drag.lastTime, 1);
    drag.velocity = (drag.lastX - event.clientX) / elapsed;
    drag.lastX = event.clientX;
    drag.lastTime = now;

    const deltaPosition = -deltaX / this.#step;
    let nextPosition = Math.max(
      drag.startPosition - 1,
      Math.min(drag.startPosition + 1, drag.startPosition + deltaPosition)
    );
    if (!this.#infinite) nextPosition = this.#constrainFinitePosition(nextPosition);
    this.#render(nextPosition);
  };

  #onPointerEnd = (event) => {
    const drag = this.#dragState;
    if (!drag || event.pointerId !== drag.pointerId) return;

    this.#dragController?.abort();
    this.#dragController = undefined;
    this.#dragState = undefined;
    this.removeAttribute('data-rift-dragging');

    if (!drag.active || drag.cancelled) {
      this.#scheduleAutoplay();
      return;
    }

    this.#viewport.releasePointerCapture?.(event.pointerId);
    this.#suppressClick = true;
    window.setTimeout(() => {
      this.#suppressClick = false;
    }, 0);

    const travelled = this.#position - drag.startPosition;
    let direction = 0;
    if (Math.abs(travelled) >= RIFT_REVIEW_DRAG_THRESHOLD) direction = Math.sign(travelled);
    else if (Math.abs(drag.velocity) >= RIFT_REVIEW_VELOCITY_THRESHOLD) direction = Math.sign(drag.velocity);

    let target = Math.round(drag.startPosition) + direction;
    if (!this.#infinite) target = this.#constrainFinitePosition(target);
    this.#animateTo(target);
  };

  #onSlideClick = (event) => {
    if (this.#suppressClick || !event.target || typeof event.target.closest !== 'function') return;
    const slide = event.target.closest('[data-rift-review-slide]');
    if (!slide) return;

    const originalIndex = Number.parseInt(
      slide.dataset.riftOriginalIndex ?? slide.dataset.riftReviewIndex ?? '0',
      10
    );
    if (originalIndex !== this.#currentIndex) this.#goTo(originalIndex);
  };

  #onKeyDown = (event) => {
    if (event.key === 'ArrowLeft') {
      event.preventDefault();
      this.#goBy(-1);
    } else if (event.key === 'ArrowRight') {
      event.preventDefault();
      this.#goBy(1);
    }
  };

  #goBy(direction) {
    if (this.#originalSlides.length <= 1) return;
    this.#clearAutoplay();
    let target = Math.round(this.#position) + direction;
    if (!this.#infinite) target = this.#constrainFinitePosition(target);
    this.#animateTo(target);
  }

  #goTo(index, instant = false) {
    const count = this.#originalSlides.length;
    if (index < 0 || index >= count) return;
    this.#clearAutoplay();

    let target = index;
    if (this.#infinite) {
      const normalizedPosition = this.#mod(this.#position, count);
      let difference = index - normalizedPosition;
      if (difference > count / 2) difference -= count;
      else if (difference < count / -2) difference += count;
      target = this.#position + difference;
    }

    this.#animateTo(target, instant);
  }

  #animateTo(target, instant = false) {
    this.#cancelAnimation();
    const from = this.#position;
    const distance = target - from;

    if (instant || this.#motionQuery.matches || Math.abs(distance) < 0.001) {
      this.#render(target);
      this.#settle(target);
      return;
    }

    const duration = this.#transitionDuration * Math.min(1.35, Math.max(0.65, Math.abs(distance)));
    const start = performance.now();

    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      this.#render(from + distance * eased);

      if (progress < 1) {
        this.#animationFrame = requestAnimationFrame(tick);
      } else {
        this.#animationFrame = undefined;
        this.#settle(target);
      }
    };

    this.#animationFrame = requestAnimationFrame(tick);
  }

  #settle(target) {
    let settled = Math.round(target);
    if (!this.#infinite) settled = this.#constrainFinitePosition(settled);

    this.#currentIndex = this.#infinite
      ? this.#mod(settled, this.#originalSlides.length)
      : settled;
    this.#position = this.#infinite ? this.#currentIndex : settled;
    this.#render(this.#position);
    this.#updateSettledState();
    this.#scheduleAutoplay();
  }

  #updateSettledState() {
    this.#originalSlides.forEach((slide, index) => {
      slide.setAttribute('aria-hidden', String(index !== this.#currentIndex));
    });

    const dots = [...this.querySelectorAll('[data-rift-dot]')];
    dots.forEach((dot, index) => {
      if (index === this.#currentIndex) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });

    const previous = this.querySelector('[data-rift-previous]');
    const next = this.querySelector('[data-rift-next]');
    if (!this.#infinite) {
      if (previous?.tagName === 'BUTTON') previous.disabled = this.#currentIndex === 0;
      if (next?.tagName === 'BUTTON') next.disabled = this.#currentIndex === this.#originalSlides.length - 1;
    }

    const status = this.querySelector('[data-rift-status]');
    const activeDot = dots[this.#currentIndex];
    if (status && activeDot?.getAttribute('aria-label')) status.textContent = activeDot.getAttribute('aria-label');
  }

  #constrainFinitePosition(position) {
    return Math.max(0, Math.min(this.#originalSlides.length - 1, position));
  }

  #mod(value, divisor) {
    return ((value % divisor) + divisor) % divisor;
  }

  #scheduleAutoplay() {
    this.#clearAutoplay();
    if (
      this.dataset.riftAutoplay !== 'true' ||
      this.#originalSlides.length <= 1 ||
      this.#motionQuery.matches ||
      this.#isHovered ||
      this.#hasFocus ||
      document.hidden
    ) return;

    const seconds = Number.parseInt(this.dataset.riftAutoplaySpeed || '6', 10) || 6;
    this.#autoplayTimer = window.setTimeout(() => this.#goBy(1), seconds * 1000);
  }

  #clearAutoplay() {
    if (this.#autoplayTimer) window.clearTimeout(this.#autoplayTimer);
    this.#autoplayTimer = undefined;
  }

  #cancelAnimation() {
    if (this.#animationFrame) cancelAnimationFrame(this.#animationFrame);
    this.#animationFrame = undefined;
  }

  #onVisibilityChange = () => {
    if (document.hidden) this.#clearAutoplay();
    else this.#scheduleAutoplay();
  };

  #onMotionPreferenceChange = () => {
    this.#cancelAnimation();
    this.#settle(Math.round(this.#position));
  };

  #onBlockSelect = (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;
    const index = this.#originalSlides.findIndex((slide) => slide.dataset.riftBlockId === blockId);
    if (index >= 0) this.#goTo(index, true);
  };
}

if (!customElements.get('rift-jewelry-reviews')) {
  customElements.define('rift-jewelry-reviews', RiftJewelryReviews);
}
